
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Users, ChevronDown, ChevronRight, Link, MapPin, Edit, Trash2 } from 'lucide-react';
import { ClubCoachManagement } from './ClubCoachManagement';

interface Club {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  organization_id: string;
  logo_url?: string;
  coach_count?: number;
  team_count?: number;
  player_count?: number;
  sport?: string | null;
  website?: string | null;
  location?: string | null;
  profile_picture_url?: string | null;
}

interface ClubCardProps {
  club: Club;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

export const ClubCard = ({
  club,
  isExpanded,
  onToggleExpansion,
  onEdit,
  onDelete,
  isDeleting
}: ClubCardProps) => {
  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-2">
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleExpansion}
              >
                {isExpanded ? 
                  <ChevronDown className="h-4 w-4" /> : 
                  <ChevronRight className="h-4 w-4" />
                }
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
          <div>
            <div className="flex items-center gap-2">
              {club.sport && (
                <span className="text-sm font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-md">
                  {club.sport}
                </span>
              )}
              <h4 className="font-medium">{club.name}</h4>
              {club.website && (
                <a 
                  href={club.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700"
                >
                  <Link className="h-4 w-4" />
                </a>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span>Created {new Date(club.created_at).toLocaleDateString()}</span>
                {club.location && (
                  <>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{club.location}</span>
                    </div>
                  </>
                )}
                {club.website && (
                  <>
                    <span>•</span>
                    <span className="text-blue-500">{club.website}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleExpansion}
          >
            <Users className="h-4 w-4 mr-2" />
            Manage Coaches
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <Collapsible open={isExpanded}>
        <CollapsibleContent className="border-t bg-muted/20 p-4">
          <ClubCoachManagement clubId={club.id} clubName={club.name} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
